/* eslint-disable @typescript-eslint/no-unsafe-return */
import { APP_PARAMS_KEY } from '@utils/integration'
import { getAdminPreviewEditionSlug } from '../dev/applyAdminPreviewQuery'
import { slugLooksLikeQuebecElection } from '@utils/editionSlug'

export enum AppType {
  ELECTIONS_CANADA = 'ELECTIONS_CANADA',
  ELECTIONS_USA = 'ELECTIONS_USA',
  /** Élections Québec 2022 (EQUE22) — slug `default` (domaine racine) ou `quebec2022` */
  ELECTIONS_QUEBEC = 'ELECTIONS_QUEBEC',
  LQUE = 'LQUE',
  /** Datagotchi Santé (SCAN26) — slug d’accès public ex. `sante` (`Category.url_slug`) */
  SANTE = 'SANTE',
}

const APP_TYPE_VALUES = new Set<string>(Object.values(AppType))

export const parseAppTypeKey = (key?: string | null): AppType | null => {
  if (!key) return null
  const normalized = key.trim().toUpperCase()
  if (APP_TYPE_VALUES.has(normalized)) {
    return normalized as AppType
  }
  return null
}

const getHostname = (): string => {
  if (window.location.port === '3000' || window.location.port === '5173') {
    return localStorage.getItem('debug:hostname') ?? import.meta.env.VITE_APP_OVERRIDE_HOSTNAME ?? ''
  }

  // if our app is deployed in a review bucket, we need to override the hostname
  // to match one of the survey's hostname
  if (window.location.hostname.includes('s3.ca-central-1.amazonaws.com')) {
    return import.meta.env.VITE_APP_OVERRIDE_HOSTNAME ?? ''
  }

  return window.location.hostname
}

/** Slug questionnaire en dev : admin preview (session), override utilisateur, env, puis debug:slug. */
const SLUG_OVERRIDE_KEY = 'debug:slug:override'

const slugFromEnvThenDebugStorage = (): string => {
  const adminEdition = getAdminPreviewEditionSlug()
  if (adminEdition) {
    return adminEdition
  }
  const override = localStorage.getItem(SLUG_OVERRIDE_KEY)?.trim()
  if (override) {
    return override
  }
  const envSlug = ((import.meta.env.VITE_APP_SLUG as string) ?? '').trim()
  if (envSlug) {
    return envSlug
  }
  return localStorage.getItem('debug:slug') ?? ''
}

export const getAppSlug = (hostname: string) => {
  // Sur les ports Vite / CRA, getHostname() peut être un faux host (debug:hostname) pour simuler
  // le déploiement — le slug API ne doit pas en dériver : il suit VITE_APP_SLUG puis debug:slug.
  const port = window.location.port
  if (port === '3000' || port === '5173') {
    return slugFromEnvThenDebugStorage()
  }

  // Localhost sans port listé ci-dessus (ex. preview sur autre port) : même règle env puis debug:slug.
  const isLocalDevHost = hostname === 'localhost' || hostname === ''
  if (isLocalDevHost) {
    return slugFromEnvThenDebugStorage()
  }

  // Return first subdomain of origin.
  // e.g. test.datagotchi.com => test
  // Used as slug to query a survey and customize the whole app.
  const slug = hostname.replace(import.meta.env.VITE_APP_BASE_HOSTNAME ?? '', '')
  const subdomain = slug.split('.')?.[0] ?? ''

  // Déploiement mono-édition servi sur le hostname de base lui-même (aucun sous-domaine
  // d'édition) : sans repli, WithSurvey n'a aucun slug à charger et reste en chargement.
  if (!subdomain) {
    return slugFromEnvThenDebugStorage()
  }

  return subdomain
}

export const getAppTypeBySubdomain = (slug?: string): AppType => {
  switch (true) {
    case slug?.includes('lque'):
    case slug?.includes('livres'):
      return AppType.LQUE
    case slug?.includes('usa'):
      return AppType.ELECTIONS_USA
    case slug === 'sante':
      return AppType.SANTE
    case slugLooksLikeQuebecElection(slug):
      return AppType.ELECTIONS_QUEBEC
    default:
      return AppType.ELECTIONS_CANADA
  }
}

export const APP_SLUG = getAppSlug(getHostname())

// Boot : heuristique slug. Après chargement survey, Category.app_type peut surcharger via setAppTypeOverride.
export let APP_TYPE = getAppTypeBySubdomain(APP_SLUG)

let appTypeOverride: AppType | null = null

const syncAppTypeFlags = () => {
  const t = APP_TYPE
  isUSAApp = t === AppType.ELECTIONS_USA
  isLiteraryApp = t === AppType.LQUE
  isCanadaApp = t === AppType.ELECTIONS_CANADA
  isQuebecApp = t === AppType.ELECTIONS_QUEBEC
  isSanteApp = t === AppType.SANTE
}

/** Prefer Category.app_type from survey payload when present (Admin 2.0). */
export const setAppTypeOverride = (key?: string | null): void => {
  const parsed = parseAppTypeKey(key)
  appTypeOverride = parsed
  APP_TYPE = parsed ?? getAppTypeBySubdomain(APP_SLUG)
  syncAppTypeFlags()
}

export const getActiveAppType = (): AppType => appTypeOverride ?? APP_TYPE

export let isUSAApp = APP_TYPE === AppType.ELECTIONS_USA
export let isLiteraryApp = APP_TYPE === AppType.LQUE
export let isCanadaApp = APP_TYPE === AppType.ELECTIONS_CANADA
export let isQuebecApp = APP_TYPE === AppType.ELECTIONS_QUEBEC
export let isSanteApp = APP_TYPE === AppType.SANTE
export const isIntegratedApp = () => {
  const params = localStorage.getItem(APP_PARAMS_KEY)
  if (!params) {
    return false
  }

  // get the src from the params
  const { src } = JSON.parse(params)

  return src != null && src !== ''
}
